import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall, Mail, MapPin, ArrowRight, CheckCircle2, Star, Sparkles, Shield, Clock, Award, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { mockData } from '../data/mock';
import QuoteModal from '../components/QuoteModal';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

const HomeVersionB = () => {
  usePageMeta('Alpha Epoxy Floors Sydney | Premium Epoxy Flooring | 35% Off', 'Sydney\'s #1 epoxy flooring specialists. Garage, commercial, warehouse, metallic & flake flooring. 15+ years experience. Free quotes. 35% off all services. Call 0451 945 455.');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling past "Our Premium Services" section
      setShowFloatingButton(window.scrollY > 2200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, message: formData.notes, source: 'homepage-hero' })
      });
      if (!res.ok) throw new Error('Failed');
      toast({
        title: "Quote Request Received!",
        description: "We'll contact you within 24 hours to discuss your project."
      });
      if (window.gtag) { window.gtag('event', 'conversion', { 'send_to': 'AW-18269270249/IPghCNXa_sQcEOnhu4dE', 'value': 1.0, 'currency': 'AUD' }); }
      setFormData({ name: '', phone: '', email: '', notes: '' });
    } catch (err) {
      toast({ title: "Error", description: "Something went wrong. Please call us at 0451 945 455.", variant: "destructive" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black">
      <Header activePage="home" />

      {/* Hero Section with Form */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden mt-28 py-20" style={{ backgroundColor: '#1C2541' }}>
        <div className="absolute inset-0">
          <img 
            src="/images/hero-bg.png" 
            alt="Alpha Epoxy Flooring Background"
            className="w-full h-full object-cover opacity-60"
            style={{ objectPosition: '30% center' }}
            loading="eager"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(28, 37, 65, 0.2) 0%, rgba(28, 37, 65, 0.4) 30%, rgba(28, 37, 65, 0.6) 60%, rgba(28, 37, 65, 0.7) 100%)' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full animate-fadeIn">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div>
              <div className="inline-block mb-6 px-6 py-3 bg-red-600 text-white rounded-full text-sm font-bold animate-pulse shadow-lg shadow-red-700/50">
                ⚡ LIMITED TIME OFFER - 35% OFF EPOXY FLOORING IN SYDNEY
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Sydney Epoxy Flooring That Looks Better
                <span className="block text-red-600 mt-2">and Lasts Longer</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                Sydney's most trusted epoxy specialists. Premium quality, 
                stunning results, guaranteed satisfaction.
              </p>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="text-center p-4 bg-red-600/10 rounded-xl border border-red-700/20">
                  <div className="text-4xl font-bold text-red-600 mb-2">15+</div>
                  <div className="text-gray-400 text-sm">Years Experience</div>
                </div>
                <div className="text-center p-4 bg-red-600/10 rounded-xl border border-red-700/20">
                  <div className="text-4xl font-bold text-red-600 mb-2">500+</div>
                  <div className="text-gray-400 text-sm">Projects Done</div>
                </div>
                <div className="text-center p-4 bg-red-600/10 rounded-xl border border-red-700/20">
                  <div className="text-4xl font-bold text-red-600 mb-2">98%</div>
                  <div className="text-gray-400 text-sm">Satisfaction</div>
                </div>
              </div>

              <div className="flex items-center space-x-4 text-gray-300">
                <CheckCircle2 className="w-6 h-6 text-red-600" />
                <span>Free Quotes</span>
                <CheckCircle2 className="w-6 h-6 text-red-600" />
                <span>5-Year Workmanship Warranty</span>
                <CheckCircle2 className="w-6 h-6 text-red-600" />
                <span>Fast Service</span>
              </div>
            </div>

            {/* Right Side - Form */}
            <Card className="border-2 border-red-600/50 shadow-2xl shadow-red-700/20 bg-white backdrop-blur-sm">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="inline-block px-4 py-2 bg-red-600 text-white rounded-full text-sm font-bold mb-4">
                    CLAIM YOUR 35% DISCOUNT
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Epoxy Flooring Quote</h2>
                  <p className="text-gray-600">Fill out the form below and we'll contact you within 24 hours</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="04XX XXX XXX"
                      required
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="h-12 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                    />
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-semibold text-gray-900 mb-2">
                      Project Details
                    </label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Tell us about your project... (e.g., garage floor, commercial space, square meters, timeline, etc.)"
                      rows={5}
                      className="resize-none bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-red-600"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-14 text-lg transition-all duration-200 hover:scale-105 shadow-lg shadow-red-700/30"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Get My 35% OFF Quote Now
                  </Button>

                  <p className="text-xs text-gray-600 text-center">
                    By submitting this form, you agree to receive communication from Alpha Epoxy Flooring Sydney
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 border-t border-gray-200" style={{ backgroundColor: '#1C2541' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Choose Alpha Epoxy?</h2>
            <p className="text-xl text-gray-300">Sydney's most trusted name in premium epoxy flooring</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.features.map((feature, index) => (
              <Card key={index} className="border-2 border-red-600/30 shadow-lg hover:shadow-red-700/30 transition-all duration-300 hover:-translate-y-2 bg-white/5 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center mx-auto mb-6 transform hover:rotate-12 transition-transform duration-300">
                    {React.cloneElement(feature.icon, { className: "w-8 h-8 text-white" })}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Premium Services</h2>
            <p className="text-xl text-gray-600">Expert epoxy solutions for every application</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.map((service, index) => (
              <Card key={index} className="group overflow-hidden border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-2">{service.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-red-700 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full border-red-700 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200">
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Epoxy Services Section */}
      <section className="py-24" style={{ backgroundColor: '#1C2541' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Premium Epoxy Flooring Services for Residential and Commercial Spaces
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-8"></div>
          </div>

          <div className="space-y-8 text-gray-300 text-lg leading-relaxed">
            <p>
              Looking for reliable epoxy flooring solutions in Sydney? <span className="text-white font-semibold">Alpha Epoxy Flooring</span> offers trusted epoxy flooring services and industrial epoxy flooring solutions throughout Sydney and surrounding areas. We specialize in premium epoxy floor coating for both residential and commercial spaces, delivering exceptional quality that stands the test of time.
            </p>

            <p>
              Many clients ask us, <span className="text-red-600 font-semibold">"What is an epoxy floor?"</span> An epoxy floor is a high-performance, resin-based surface chemically bonded to concrete, offering unmatched durability, chemical resistance, and a beautifully polished finish. Unlike traditional flooring options, epoxy creates a seamless, non-porous surface that's incredibly easy to maintain while providing superior protection against heavy traffic, spills, and daily wear.
            </p>

            <div className="bg-black/30 border-l-4 border-red-600 p-6 rounded-r-lg">
              <h3 className="text-2xl font-bold text-white mb-4">Our Comprehensive Epoxy Solutions Include:</h3>
              <ul className="grid md:grid-cols-2 gap-4">
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span><strong className="text-white">Garage Epoxy Flooring:</strong> Transform your garage into a showroom-quality space</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span><strong className="text-white">Color Flake Epoxy:</strong> Decorative, slip-resistant finishes with endless design options</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span><strong className="text-white">Commercial Kitchen Epoxy:</strong> Food-safe, HACCP-compliant flooring solutions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span><strong className="text-white">Metallic Epoxy:</strong> Stunning 3D effects for modern, upscale spaces</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span><strong className="text-white">Warehouse Flooring:</strong> Heavy-duty solutions for high-traffic industrial areas</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-600 mr-2">✓</span>
                  <span><strong className="text-white">Polished Concrete:</strong> Natural, eco-friendly finishes with low maintenance</span>
                </li>
              </ul>
            </div>

            <p>
              Our team excels in <span className="text-white font-semibold">metallic epoxy flooring</span> and <span className="text-white font-semibold">metallic epoxy floor coating</span>, delivering durable, visually striking surfaces that create unique marble-like patterns and depth effects. These premium finishes are perfect for showrooms, retail spaces, modern homes, and any environment where aesthetics matter as much as performance.
            </p>

            <p>
              From complete concrete coating services to custom epoxy solutions tailored to your specific needs, we bring <span className="text-red-600">15+ years of expertise</span>, premium Australian-grade materials, and lasting results to every project. Our professional team uses industrial-grade diamond grinding equipment to ensure perfect surface preparation, followed by precise application techniques that guarantee a flawless, long-lasting finish.
            </p>

            <div className="bg-red-600/10 border border-red-600/30 p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-4">Why Sydney Businesses & Homeowners Choose Us:</h3>
              <div className="grid md:grid-cols-3 gap-6 text-base">
                <div>
                  <div className="text-red-600 font-bold text-xl mb-2">15+ Years</div>
                  <p>Proven expertise serving Sydney's residential and commercial sectors</p>
                </div>
                <div>
                  <div className="text-red-600 font-bold text-xl mb-2">500+ Projects</div>
                  <p>Successfully completed with 98% customer satisfaction rate</p>
                </div>
                <div>
                  <div className="text-red-600 font-bold text-xl mb-2">5-Year Workmanship Warranty</div>
                  <p>Comprehensive coverage on all epoxy installations</p>
                </div>
              </div>
            </div>

            <p className="text-center text-xl">
              Whether you need a stunning <span className="text-white">residential garage transformation</span>, a durable <span className="text-white">commercial kitchen floor</span>, or a high-performance <span className="text-white">industrial warehouse solution</span>, we deliver excellence on every project. Our eco-friendly, low-VOC formulations ensure safety for your family, employees, and customers while providing the ultimate in durability and beauty.
            </p>
          </div>

          <div className="text-center mt-12">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white font-bold px-12 py-6 text-xl transition-all duration-200 hover:scale-105 shadow-lg shadow-red-700/30" onClick={() => setIsModalOpen(true)}>
              <PhoneCall className="w-6 h-6 mr-3" />
              Get Your Epoxy Flooring Quote Now
            </Button>
            <p className="text-gray-400 mt-4 text-sm">Free consultation • No obligation • Fast response within 24 hours</p>
          </div>
        </div>
      </section>

      {/* Portfolio Section - Asymmetric Grid */}
      <section id="gallery" className="py-24 bg-white" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Portfolio</h2>
            <p className="text-xl text-gray-600">See the quality that sets us apart</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Row 1: Large left + Medium right */}
            <div className="md:col-span-7 group relative overflow-hidden rounded-2xl cursor-pointer" style={{ height: '420px' }}>
              <img 
                src={mockData.gallery[0].url} 
                alt={mockData.gallery[0].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full mb-2">{mockData.gallery[0].category}</span>
                <h3 className="text-2xl font-bold text-white">{mockData.gallery[0].title}</h3>
              </div>
            </div>
            <div className="md:col-span-5 group relative overflow-hidden rounded-2xl cursor-pointer" style={{ height: '420px' }}>
              <img 
                src={mockData.gallery[1].url} 
                alt={mockData.gallery[1].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full mb-2">{mockData.gallery[1].category}</span>
                <h3 className="text-xl font-bold text-white">{mockData.gallery[1].title}</h3>
              </div>
            </div>

            {/* Row 2: Three equal columns */}
            <div className="md:col-span-4 group relative overflow-hidden rounded-2xl cursor-pointer" style={{ height: '320px' }}>
              <img 
                src={mockData.gallery[2].url} 
                alt={mockData.gallery[2].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full mb-2">{mockData.gallery[2].category}</span>
                <h3 className="text-lg font-bold text-white">{mockData.gallery[2].title}</h3>
              </div>
            </div>
            <div className="md:col-span-4 group relative overflow-hidden rounded-2xl cursor-pointer" style={{ height: '320px' }}>
              <img 
                src={mockData.gallery[3].url} 
                alt={mockData.gallery[3].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full mb-2">{mockData.gallery[3].category}</span>
                <h3 className="text-lg font-bold text-white">{mockData.gallery[3].title}</h3>
              </div>
            </div>
            <div className="md:col-span-4 group relative overflow-hidden rounded-2xl cursor-pointer" style={{ height: '320px' }}>
              <img 
                src={mockData.gallery[4].url} 
                alt={mockData.gallery[4].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full mb-2">{mockData.gallery[4].category}</span>
                <h3 className="text-lg font-bold text-white">{mockData.gallery[4].title}</h3>
              </div>
            </div>

            {/* Row 3: Full width feature */}
            <div className="md:col-span-12 group relative overflow-hidden rounded-2xl cursor-pointer" style={{ height: '360px' }}>
              <img 
                src={mockData.gallery[5].url} 
                alt={mockData.gallery[5].title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <span className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full mb-2">{mockData.gallery[5].category}</span>
                <h3 className="text-2xl font-bold text-white">{mockData.gallery[5].title}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="testimonials" className="py-24 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Real reviews from real Sydney customers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-testid="review-cards">
            {mockData.testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`review-card-${index}`}>
                {/* Review Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ backgroundColor: ['#4285F4', '#EA4335', '#34A853'][index % 3] }}>
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900">{testimonial.name}</p>
                    <p className="text-xs text-gray-500">{testimonial.date}</p>
                  </div>
                  {/* Small Google icon */}
                  <svg viewBox="0 0 24 24" width="20" height="20" className="flex-shrink-0">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                {/* Star Rating */}
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                {/* Review Text */}
                <p className="text-sm text-gray-700 leading-relaxed">{testimonial.review}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">Ready to transform your floors? Contact us today!</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-red-600/50 bg-white text-center hover:border-red-700 transition-all duration-300 shadow-lg hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PhoneCall className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Phone</h3>
                <p className="text-gray-600">0451 945 455</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-600/50 bg-white text-center hover:border-red-700 transition-all duration-300 shadow-lg hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
                <p className="text-gray-600">info@alphaepoxy.com.au</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-600/50 bg-white text-center hover:border-red-700 transition-all duration-300 shadow-lg hover:shadow-xl">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Location</h3>
                <p className="text-gray-600">Sydney, NSW, Australia</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-gray-50" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Serving All of Sydney</h2><p className="text-xl text-gray-600 max-w-3xl mx-auto">From North Shore to Southern Sydney, Eastern Suburbs to Western Sydney — we bring premium epoxy flooring solutions to your doorstep</p></div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-red-600 h-[500px]"><iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d424143.94532033163!2d150.65179054788284!3d-33.84714479524214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b129838f39a743f%3A0x3017d681632a850!2sSydney%20NSW%2C%20Australia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sydney Service Area Map"></iframe></div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Coverage Across Greater Sydney</h3>
              <p className="text-gray-600 text-lg mb-8">No matter where you are in Sydney, our expert team is ready to transform your space with premium epoxy flooring solutions.</p>
              <div className="grid grid-cols-2 gap-4 mb-8">{["Northern Beaches","North Shore","Inner West","Eastern Suburbs","Western Sydney","Southern Sydney","Hills District","Sutherland Shire","Parramatta Area","Canterbury-Bankstown","Blacktown","Penrith"].map((area,i)=>(<div key={i} className="flex items-center"><CheckCircle2 className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" /><span className="text-gray-700 font-medium">{area}</span></div>))}</div>
              <Card className="border-2 border-red-600/50 bg-red-50"><CardContent className="p-6"><h4 className="text-xl font-bold text-gray-900 mb-3">Free On-Site Assessment</h4><p className="text-gray-700 mb-4">We'll visit your location anywhere in Sydney for a free, no-obligation consultation and provide an accurate quote on the spot.</p><Button className="bg-red-600 hover:bg-red-700 text-white font-bold"><PhoneCall className="w-4 h-4 mr-2" />Book Your Free Visit</Button></CardContent></Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Below Map */}
      <section className="py-24 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about epoxy flooring</p>
          </div>

          <div className="space-y-4">
            {mockData.faqs.map((faq, index) => (
              <Card key={index} className="border border-gray-200 shadow-md hover:shadow-lg transition-all duration-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Floating Quote Button */}
      {showFloatingButton && (
        <button
          onClick={() => setIsModalOpen(true)}
          className="fixed right-0 top-1/2 -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white font-bold py-6 px-4 rounded-l-lg shadow-2xl z-50 transition-all duration-300 hover:px-6 animate-slideInRight"
          style={{ writingMode: 'vertical-rl' }}
        >
          <span className="flex items-center gap-2">
            <PhoneCall className="w-5 h-5" />
            GET A QUOTE NOW
          </span>
        </button>
      )}

      {/* Quote Modal */}
      <QuoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} version="B" />
    </div>
  );
};

export default HomeVersionB;